// Every configured agent, which departments they answer for, and whether they
// currently take calls.

import { useMemo, useState } from 'react';
import { useGuide } from './context';
import { nodeName } from './model';
import { SectionHead, EmptyNote, PresenceDot, PresenceConsentHint } from './components';
import { presenceLabel } from './lib/presence';

export function PeopleSection() {
  const { t, model, lang, dirById, navigate, presence, presenceState } = useGuide();
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const out: Array<{ id: string; name: string; email?: string; queues: Array<{ id: string; name: string; optIn?: boolean }> }> = [];
    for (const id of model.agentIds) {
      const d = dirById[id];
      const queues = (model.queuesByAgent.get(id) || []).map(queue => ({
        id: queue.id, name: nodeName(queue, lang), optIn: queue.agents?.find(a => a.id.toLowerCase() === id)?.optIn,
      }));
      out.push({ id, name: d?.displayName || `${id.slice(0, 8)}…`, email: d?.email, queues });
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    const s = q.trim().toLowerCase();
    return s ? out.filter(r => r.name.toLowerCase().includes(s) || (r.email || '').toLowerCase().includes(s) || r.queues.some(x => x.name.toLowerCase().includes(s))) : out;
  }, [model, dirById, lang, q]);

  return (
    <div className="space-y-3">
      <SectionHead title={t('peopleTitle')} intro={t('peopleIntro')} actions={
        <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder={t('filterHere')}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 w-56" />
      } />
      <PresenceConsentHint />
      {rows.length === 0 ? <EmptyNote text={t('noPeople')} /> : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium">{t('person')}</th>
                <th className="px-3 py-2 font-medium hidden sm:table-cell">{t('email')}</th>
                <th className="px-3 py-2 font-medium">{t('department')}</th>
                <th className="px-3 py-2 font-medium">{t('active')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="px-3 py-2 text-gray-900">
                    <span className="inline-flex items-center gap-2">
                      {presenceState === 'ok' && <PresenceDot info={presence?.get(r.id)} />}
                      <span className="font-medium">{r.name}</span>
                    </span>
                    {presenceState === 'ok' && <div className="text-[11px] text-gray-500 sm:hidden">{presenceLabel(t, presence?.get(r.id))}</div>}
                  </td>
                  <td className="px-3 py-2 text-gray-500 hidden sm:table-cell">{r.email || ''}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.queues.map(x => (
                        <button key={x.id} type="button" onClick={() => navigate({ section: 'departments', id: x.id })}
                          className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200">{x.name}</button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">{r.queues.every(x => x.optIn !== false) ? t('yes') : t('optedOut')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
